import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldErrors, Resolver } from 'react-hook-form';
import { useForm as useReactHookForm, useWatch } from 'react-hook-form';
import type { FormErrors, FormValues } from 'src/lib/components/form/formTypes';
import { toFormValidate, ZodSchemaLike } from 'src/lib/components/form/zodAdapter';

const isZodSchemaLike = (schema: unknown): schema is ZodSchemaLike =>
    typeof schema === 'object' &&
    schema !== null &&
    typeof (schema as { parseAsync?: unknown }).parseAsync === 'function' &&
    typeof (schema as { safeParseAsync?: unknown }).safeParseAsync === 'function';

export interface FormContext<T extends FormValues> {
    initialValues: T;
    values: T;
    errors: FormErrors<T>;
    touched: Record<string, any>;
    submitCount: number;
    isSubmitting: boolean;
    isValid: boolean;
    dirty: boolean;
    handleBlur: (eventOrString: any) => void | ((e: any) => void);
    handleChange: (
        eventOrPath: string | React.ChangeEvent<any>,
    ) => void | ((eventOrTextValue: string | React.ChangeEvent<any>) => void);
    handleReset: (e?: any) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
    resetForm: (nextState?: Partial<{ values: T }> | undefined) => void;
    setErrors: (errors: FormErrors<T>) => void;
    setFieldTouched: (
        field: string,
        touched?: boolean,
        shouldValidate?: boolean | undefined,
    ) => any;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => any;
    setFieldError: (field: string, value: string | undefined) => void;
    setSubmitting: (isSubmitting: boolean) => void;
    setTouched: (touched: Record<string, any>, shouldValidate?: boolean | undefined) => any;
    setValues: (values: T | ((values: T) => T), shouldValidate?: boolean | undefined) => any;
    submitForm: () => Promise<void | undefined>;
    validateForm: (values?: T) => Promise<FormErrors<T>>;
    validationSchemaZod: any;
    resetFormRef: React.MutableRefObject<(nextState?: Partial<{ values: T }> | undefined) => void>;
    rhf: ReturnType<typeof useReactHookForm<T>>;
}

type FormConfig<Values extends FormValues> = {
    initialValues: Values;
    onSubmit?: (
        values: Values,
        helpers: {
            resetForm: (nextState?: Partial<{ values: Values }> | undefined) => void;
            setErrors: (errors: FormErrors<Values>) => void;
            setFieldError: (field: string, value: string | undefined) => void;
            setFieldTouched: (
                field: string,
                touched?: boolean,
                shouldValidate?: boolean | undefined,
            ) => any;
            setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => any;
            setSubmitting: (isSubmitting: boolean) => void;
            setTouched: (touched: Record<string, any>, shouldValidate?: boolean | undefined) => any;
            setValues: (
                values: Values | ((values: Values) => Values),
                shouldValidate?: boolean | undefined,
            ) => any;
            validateForm: (values?: Values) => Promise<FormErrors<Values>>;
        },
    ) => any;
    validate?: (values: Values) => void | FormErrors<Values> | Promise<void | FormErrors<Values>>;
    validationSchema?: unknown;
    enableReinitialize?: boolean;
    validateOnBlur?: boolean;
    validateOnChange?: boolean;
    validateOnMount?: boolean;
};

type RHFErrorObject = FieldErrors<any>;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    if (Object.prototype.toString.call(value) !== '[object Object]') {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

const getFieldValueFromChangeEvent = (event: React.ChangeEvent<any>) => {
    const target = event.target as any;
    const type = target?.type as string | undefined;

    if (type === 'checkbox') {
        return Boolean(target?.checked);
    }

    if (type === 'number' || type === 'range') {
        const parsed = Number.parseFloat(String(target?.value ?? ''));
        return Number.isNaN(parsed) ? '' : parsed;
    }

    if (type === 'select-multiple' || Boolean(target?.multiple)) {
        const options = target?.options;
        if (!options) {
            return [];
        }
        const selected: string[] = [];
        for (const option of Array.from(options as ArrayLike<any>)) {
            if (option?.selected) {
                selected.push(String(option.value));
            }
        }
        return selected;
    }

    return target?.value;
};

const fieldErrorsToFormErrors = <TValues extends FormValues>(
    errors: RHFErrorObject,
): FormErrors<TValues> => {
    const walk = (node: any): any => {
        if (!node) {
            return undefined;
        }
        if (typeof node.message === 'string') {
            return node.message;
        }
        if (Array.isArray(node)) {
            return node.map(walk);
        }
        if (isPlainObject(node)) {
            const out: Record<string, any> = {};
            for (const key of Object.keys(node)) {
                const v = walk(node[key]);
                if (v !== undefined) {
                    out[key] = v;
                }
            }
            return Object.keys(out).length ? out : undefined;
        }
        return undefined;
    };

    return (walk(errors) ?? {}) as FormErrors<TValues>;
};

const formErrorsToFieldErrors = (errors: FormErrors<any>): RHFErrorObject => {
    const out: RHFErrorObject = {} as RHFErrorObject;
    const setNested = (path: string[], message: string) => {
        if (path.length === 0) {
            return;
        }
        let cursor: RHFErrorObject = out;
        for (let i = 0; i < path.length - 1; i++) {
            const key = path[i];
            const next = (cursor as any)[key];
            if (!isPlainObject(next)) {
                (cursor as any)[key] = {};
            }
            cursor = (cursor as any)[key] as RHFErrorObject;
        }
        (cursor as any)[path[path.length - 1]] = { type: 'manual', message };
    };

    const walk = (node: any, path: string[]) => {
        if (node == null) {
            return;
        }
        if (typeof node === 'string') {
            setNested(path, node);
            return;
        }
        if (Array.isArray(node)) {
            node.forEach((child, idx) => walk(child, [...path, String(idx)]));
            return;
        }
        if (isPlainObject(node)) {
            for (const key of Object.keys(node)) {
                walk(node[key], [...path, key]);
            }
        }
    };

    walk(errors, []);
    return out;
};

export default function useForm<Values extends FormValues = FormValues>(
    formConfig: FormConfig<Values>,
): FormContext<Values> {
    const initialValuesRef = useRef(formConfig.initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formConfigRef = useRef(formConfig);
    formConfigRef.current = formConfig;
    const submitCountRef = useRef(0);

    const validationConfig = useMemo(() => {
        const schemaOrFactory = formConfig.validationSchema;
        if (!schemaOrFactory) {
            return { validate: formConfig.validate };
        }

        let resolvedSchema: unknown = schemaOrFactory;
        if (typeof schemaOrFactory === 'function') {
            try {
                resolvedSchema = schemaOrFactory();
            } catch {
                resolvedSchema = schemaOrFactory;
            }
        }

        if (isZodSchemaLike(resolvedSchema)) {
            const zodValidate = toFormValidate(resolvedSchema);
            if (!formConfig.validate) {
                return { validate: zodValidate };
            }
            return {
                validate: async (values: Values) => {
                    const [zodErrors, customErrors] = await Promise.all([
                        zodValidate(values as any),
                        formConfig.validate?.(values),
                    ]);
                    if (!zodErrors) {
                        return customErrors as any;
                    }
                    if (!customErrors) {
                        return zodErrors as any;
                    }
                    return { ...(zodErrors as any), ...(customErrors as any) };
                },
            };
        }

        return { validate: formConfig.validate };
    }, [formConfig.validate, formConfig.validationSchema]);
    const validationConfigRef = useRef(validationConfig);
    validationConfigRef.current = validationConfig;

    const mode = useMemo(() => {
        if (formConfig.validateOnChange) {
            return 'onChange' as const;
        }
        if (formConfig.validateOnBlur) {
            return 'onBlur' as const;
        }
        return 'onSubmit' as const;
    }, [formConfig.validateOnBlur, formConfig.validateOnChange]);

    const reValidateMode = useMemo(() => {
        if (formConfig.validateOnChange === false && formConfig.validateOnBlur === false) {
            return 'onSubmit' as const;
        }
        if (formConfig.validateOnChange === false && formConfig.validateOnBlur) {
            return 'onBlur' as const;
        }
        return 'onChange' as const;
    }, [formConfig.validateOnBlur, formConfig.validateOnChange]);

    const resolver = useCallback<Resolver<Values>>(async (values) => {
        const validate = validationConfigRef.current.validate;
        if (!validate) {
            return { values, errors: {} };
        }
        const errors = (await validate(values)) as FormErrors<Values> | void;
        if (!errors) {
            return { values, errors: {} };
        }
        return {
            values: {} as any,
            errors: formErrorsToFieldErrors(errors) as FieldErrors<Values>,
        };
    }, []);

    const rhf = useReactHookForm<Values>({
        defaultValues: initialValuesRef.current as any,
        mode,
        reValidateMode,
        resolver,
    });

    const watchedValues = useWatch({ control: rhf.control }) as Values | undefined;
    const values = (watchedValues ?? rhf.getValues()) as Values;

    const submitCount = rhf.formState.submitCount;
    submitCountRef.current = submitCount;
    const touched = rhf.formState.touchedFields as Record<string, any>;
    const errors = fieldErrorsToFormErrors<Values>(rhf.formState.errors as any);

    const dirty = useMemo(() => !isEqual(initialValuesRef.current, values), [values]);
    const isValid = useMemo(() => Object.keys(errors as any).length === 0, [errors]);

    const setSubmitting = useCallback((nextIsSubmitting: boolean) => {
        setIsSubmitting(nextIsSubmitting);
    }, []);

    const setFieldError = useCallback(
        (field: string, value: string | undefined) => {
            if (!value) {
                rhf.clearErrors(field as any);
                return;
            }
            rhf.setError(field as any, { type: 'manual', message: value });
        },
        [rhf],
    );

    const setErrors = useCallback(
        (nextErrors: FormErrors<Values>) => {
            rhf.clearErrors();
            const fieldErrors = formErrorsToFieldErrors(nextErrors);
            const applyErrors = (node: any, path: string[]) => {
                if (!node) {
                    return;
                }
                if (typeof node.message === 'string') {
                    rhf.setError(path.join('.') as any, { type: 'manual', message: node.message });
                    return;
                }
                if (isPlainObject(node)) {
                    for (const key of Object.keys(node)) {
                        applyErrors(node[key], [...path, key]);
                    }
                }
            };
            applyErrors(fieldErrors, []);
        },
        [rhf],
    );

    const validateForm = useCallback(
        async (nextValues?: Values) => {
            const valuesToValidate = (nextValues ?? rhf.getValues()) as Values;
            const validate = validationConfigRef.current.validate;
            if (!validate) {
                return {} as FormErrors<Values>;
            }
            const nextErrors = (await validate(valuesToValidate)) as FormErrors<Values> | void;
            if (nextErrors) {
                setErrors(nextErrors as FormErrors<Values>);
            } else {
                rhf.clearErrors();
            }
            return (nextErrors ?? {}) as FormErrors<Values>;
        },
        [rhf, setErrors],
    );

    const setFieldValue = useCallback(
        (field: string, value: any, shouldValidate?: boolean) => {
            const validateOnChange = formConfigRef.current.validateOnChange;
            const resolvedShouldValidate =
                shouldValidate ??
                validateOnChange ??
                (submitCountRef.current > 0 && validateOnChange !== false);
            rhf.setValue(field as any, value, {
                shouldDirty: true,
                shouldValidate: resolvedShouldValidate,
            });
        },
        [rhf],
    );

    const setValues = useCallback(
        (nextValues: Values | ((values: Values) => Values), shouldValidate?: boolean) => {
            const resolvedValues =
                typeof nextValues === 'function'
                    ? (nextValues as (values: Values) => Values)(rhf.getValues() as Values)
                    : nextValues;

            // Keep initialValues stable while still updating the current form state.
            // If callers want to reset initial values, they should use resetForm({ values }).
            const validateOnChange = formConfigRef.current.validateOnChange;
            const resolvedShouldValidate =
                shouldValidate ??
                validateOnChange ??
                (submitCountRef.current > 0 && validateOnChange !== false);

            const currentValues = rhf.getValues() as Values;

            for (const key of Object.keys(resolvedValues) as Array<keyof Values & string>) {
                const nextValue = resolvedValues[key];
                const currentValue = currentValues[key];
                if (isEqual(nextValue, currentValue)) {
                    continue;
                }
                rhf.setValue(key as any, nextValue as any, {
                    shouldDirty: true,
                    shouldValidate: false,
                });
            }

            if (resolvedShouldValidate) {
                return validateForm(resolvedValues);
            }
            return undefined;
        },
        [rhf, validateForm],
    );

    const setFieldTouched = useCallback(
        (field: string, isTouched?: boolean, shouldValidate?: boolean) => {
            const validateOnBlur = formConfigRef.current.validateOnBlur;
            const resolvedShouldValidate =
                shouldValidate ??
                validateOnBlur ??
                (submitCountRef.current > 0 && validateOnBlur !== false);

            // RHF doesn't have a native setTouched API. The only way to mark a field
            // as touched is via setValue with shouldTouch: true, but that can fail
            // on frozen objects (GraphQL responses) or break class instances.
            //
            // Instead, we trigger validation if requested. Most code that calls
            // setFieldTouched is trying to show validation errors, which trigger() achieves.
            if (resolvedShouldValidate) {
                void rhf.trigger(field as any);
            }
        },
        [rhf],
    );

    const setTouched = useCallback(
        (nextTouched: Record<string, any>, shouldValidate?: boolean) => {
            const visit = (node: any, path: string[]) => {
                if (!node) {
                    return;
                }
                if (node === true) {
                    setFieldTouched(path.join('.'), true, shouldValidate);
                    return;
                }
                if (Array.isArray(node)) {
                    node.forEach((child, idx) => visit(child, [...path, String(idx)]));
                    return;
                }
                if (isPlainObject(node)) {
                    for (const key of Object.keys(node)) {
                        visit(node[key], [...path, key]);
                    }
                }
            };
            visit(nextTouched, []);
        },
        [setFieldTouched],
    );

    const resetForm = useCallback(
        (nextState?: Partial<{ values: Values }>) => {
            const nextValues = nextState?.values ?? initialValuesRef.current;
            initialValuesRef.current = nextValues;
            rhf.reset(nextValues);
            rhf.clearErrors();
        },
        [rhf],
    );

    const resetFormRef = useRef(resetForm);
    resetFormRef.current = resetForm;

    const submitImpl = useCallback(
        async (e?: React.FormEvent<HTMLFormElement>) => {
            setIsSubmitting(true);
            try {
                // Note: We intentionally don't call touchAllFields here.
                // RHF's handleSubmit already validates all fields and shows errors.
                // Manually touching all fields via setValue can fail on frozen objects
                // (e.g., GraphQL responses, React Compiler optimizations).
                await rhf.handleSubmit(
                    async (submittedValues) => {
                        await formConfigRef.current.onSubmit?.(submittedValues, {
                            resetForm,
                            setErrors,
                            setFieldError,
                            setFieldTouched,
                            setFieldValue,
                            setSubmitting: setIsSubmitting,
                            setTouched,
                            setValues,
                            validateForm,
                        });
                    },
                    async () => {
                        // No-op; errors are already populated by the resolver and we're not
                        // manually touching fields here to avoid mutating frozen inputs.
                    },
                )(e as any);
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            resetForm,
            rhf,
            setErrors,
            setFieldError,
            setFieldTouched,
            setFieldValue,
            setTouched,
            setValues,
            validateForm,
        ],
    );

    const submitForm = useCallback(async () => {
        await submitImpl();
    }, [submitImpl]);

    const handleSubmit = useCallback(
        (e?: React.FormEvent<HTMLFormElement>) => {
            void submitImpl(e);
        },
        [submitImpl],
    );

    const handleChange = useCallback(
        (eventOrPath: string | React.ChangeEvent<any>) => {
            if (typeof eventOrPath === 'string') {
                const path = eventOrPath;
                return (eventOrTextValue: string | React.ChangeEvent<any>) => {
                    if (
                        typeof eventOrTextValue === 'object' &&
                        eventOrTextValue &&
                        'target' in eventOrTextValue
                    ) {
                        const event = eventOrTextValue as React.ChangeEvent<any>;
                        setFieldValue(path, getFieldValueFromChangeEvent(event));
                        return;
                    }
                    setFieldValue(path, eventOrTextValue);
                };
            }

            const event = eventOrPath;
            const name = event.target.name;
            setFieldValue(name, getFieldValueFromChangeEvent(event));
        },
        [setFieldValue],
    );

    const handleBlur = useCallback(
        (eventOrString: any) => {
            if (typeof eventOrString === 'string') {
                const path = eventOrString;
                return () => setFieldTouched(path, true);
            }
            const event = eventOrString as React.FocusEvent<any>;
            const name = (event.target as any)?.name;
            if (name) {
                setFieldTouched(name, true);
            }
        },
        [setFieldTouched],
    );

    const handleReset = useCallback(
        (e?: any) => {
            e?.preventDefault?.();
            resetForm();
        },
        [resetForm],
    );

    useEffect(() => {
        if (!formConfig.validateOnMount) {
            return;
        }
        void validateForm();
    }, [formConfig.validateOnMount, validateForm]);

    useEffect(() => {
        if (!formConfig.enableReinitialize) {
            return;
        }
        if (isEqual(formConfig.initialValues, initialValuesRef.current)) {
            return;
        }
        resetForm({ values: formConfig.initialValues });
    }, [formConfig.enableReinitialize, formConfig.initialValues, resetForm]);

    return {
        initialValues: initialValuesRef.current,
        values,
        errors,
        touched,
        submitCount,
        isSubmitting,
        isValid,
        dirty,
        handleBlur,
        handleChange,
        handleReset,
        handleSubmit,
        resetForm,
        setErrors,
        setFieldTouched,
        setFieldValue,
        setFieldError,
        setSubmitting,
        setTouched,
        setValues,
        submitForm,
        validateForm,
        resetFormRef: resetFormRef,
        validationSchemaZod: formConfig.validationSchema,
        rhf,
    };
}

export const useScrollToError = (isSubmitting: boolean) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isSubmitting && containerRef.current) {
            const errorElement = containerRef.current.querySelector('.Mui-error');
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [isSubmitting]);

    return { containerRef };
};
